import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  RefreshCw,
  X,
  Plus,
  Truck,
  XCircle,
  MoreVertical,
  Eye,
  Pencil,
  Calendar,
  CheckCircle2,
  Trash2,
  Download,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileSpreadsheet,
  Copy,
  Globe,
} from 'lucide-react';
import salesService from '../services/salesService';
import authService from '../services/authService';
import useFilterPersistence from '../hooks/useFilterPersistence';
import Layout from '../components/Layout';
import TemplateSelector from '../components/TemplateSelector';
import excelExportService from '../services/excelExportService';
import SalesImportDialog from '../components/SalesImportDialog';

const SalesPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Calculate default date range (last 30 days)
  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  // Use filter persistence hook
  const [persistedState, setPersistedState] = useFilterPersistence('sales-orders', {
    filters: {
      sales_order_id: '',
      client_name: '',
      status: ''
    },
    dateRange: getDefaultDateRange(),
    pagination: {
      page: 1,
      page_size: 10,
      total_count: 0,
      total_pages: 0
    },
    sorting: {
      orderBy: 'created_at',
      order: 'desc'
    },
    tradeTypeFilter: 'all',
    clientTypeFilter: 'domestic'
  });

  // Extract state from persisted state
  const filters = persistedState.filters;
  const dateRange = persistedState.dateRange;
  const pagination = persistedState.pagination;
  const sorting = persistedState.sorting;
  const tradeTypeFilter = persistedState.tradeTypeFilter;
  const clientTypeFilter = persistedState.clientTypeFilter;

  // Update functions
  const setFilters = (newFilters) => {
    setPersistedState(prev => ({
      ...prev,
      filters: typeof newFilters === 'function' ? newFilters(prev.filters) : newFilters
    }));
  };

  const setDateRange = (newDateRange) => {
    setPersistedState(prev => ({
      ...prev,
      dateRange: typeof newDateRange === 'function' ? newDateRange(prev.dateRange) : newDateRange
    }));
  };

  const setPagination = useCallback((newPagination) => {
    setPersistedState(prev => ({
      ...prev,
      pagination: typeof newPagination === 'function' ? newPagination(prev.pagination) : newPagination
    }));
  }, [setPersistedState]);

  const setSorting = (newSorting) => {
    setPersistedState(prev => ({
      ...prev,
      sorting: typeof newSorting === 'function' ? newSorting(prev.sorting) : newSorting
    }));
  };

  const setTradeTypeFilter = (newTradeType) => {
    setPersistedState(prev => ({
      ...prev,
      tradeTypeFilter: typeof newTradeType === 'function' ? newTradeType(prev.tradeTypeFilter) : newTradeType
    }));
  };

  const setClientTypeFilter = (newClientType) => {
    setPersistedState(prev => ({
      ...prev,
      clientTypeFilter: typeof newClientType === 'function' ? newClientType(prev.clientTypeFilter) : newClientType
    }));
  };

  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', order: null });
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // User role check for admin features
  const [userRole, setUserRole] = useState('');
  const [user, setUser] = useState(null);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await salesService.getSalesOrders(
        pagination.page,
        pagination.page_size,
        {
          ...filters,
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          sort_by: sorting.orderBy,
          sort_order: sorting.order,
          client_type: clientTypeFilter
        }
      );

      // Handle different response structures
      if (response.orders && Array.isArray(response.orders)) {
        setOrders(response.orders);
      } else if (Array.isArray(response)) {
        setOrders(response);
      } else {
        console.error('Unexpected response structure:', response);
        setOrders([]);
      }

      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          ...response.pagination
        }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load sales orders');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.page_size, filters, sorting, dateRange, clientTypeFilter, setPagination]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Load user role on component mount
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    setUserRole(currentUser?.role || '');
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterSubmit = (e) => {
    if (e) e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: ''
    }));
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearAllFilters = () => {
    setFilters({
      sales_order_id: '',
      client_name: '',
      status: ''
    });
    setDateRange(getDefaultDateRange());
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSort = (property) => {
    const isAsc = sorting.orderBy === property && sorting.order === 'asc';
    setSorting({
      orderBy: property,
      order: isAsc ? 'desc' : 'asc'
    });
  };

  const handleAction = (action, order) => {
    if (action === 'view') {
      navigate(`/sales/${order._id}/view`, { state: { orderData: order } });
    } else if (action === 'edit') {
      navigate(`/sales/${order._id}/edit`);
    } else if (action === 'clone') {
      // Clone order: copy order data but exclude _id, status, and related records
      const clonedOrder = {
        ...order,
        _id: undefined,
        sales_order_id: undefined,
        status: 'draft',
        created_at: undefined,
        updated_at: undefined,
        // Exclude purchase orders, production orders, and other related records
        purchase_orders: undefined,
        production_orders: undefined,
        linked_purchase_orders: undefined,
        so_po_mappings: undefined,
        receipts: undefined,
        shipments: undefined,
        // Keep products but ensure they are clean copies
        products: order.products?.map(p => ({
          ...p,
          _id: undefined,
          line_id: undefined,
          matched_product_id: undefined,
          allocated_quantity: undefined,
          remaining_quantity: undefined,
          production_status: undefined
        })) || []
      };
      navigate('/sales/new', { state: { clonedOrder } });
    } else if (['ship', 'cancel', 'confirm', 'delete'].includes(action)) {
      setConfirmDialog({ open: true, action, order });
    }
  };

  const handleConfirmAction = async () => {
    const { action, order } = confirmDialog;
    setConfirmDialog({ open: false, action: '', order: null });

    try {
      if (action === 'ship') {
        await salesService.shipOrder(order._id);
        setOrders(prev => prev.map(o =>
          o._id === order._id ? { ...o, status: 'shipped' } : o
        ));
      } else if (action === 'cancel') {
        await salesService.cancelSalesOrder(order._id);
        setOrders(prev => prev.map(o =>
          o._id === order._id ? { ...o, status: 'cancelled' } : o
        ));
      } else if (action === 'confirm') {
        await salesService.confirmSalesOrder(order._id);
        setOrders(prev => prev.map(o =>
          o._id === order._id ? { ...o, status: 'confirmed' } : o
        ));
      } else if (action === 'delete') {
        await salesService.deleteSalesOrder(order._id);
        // Remove the deleted order from the list
        setOrders(prev => prev.filter(o => o._id !== order._id));
        // Update pagination count
        setPagination(prev => ({
          ...prev,
          total_count: prev.total_count - 1
        }));
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} order`);
    }
  };

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'default';
      case 'shipped': return 'secondary';
      case 'delivered': return 'default';
      case 'cancelled': return 'destructive';
      default: return 'outline';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'new': return '新建';
      case 'draft': return '草稿';
      case 'pending': return '待确认';
      case 'confirmed': return '已确认';
      case 'shipped': return '已发货';
      case 'delivered': return '已送达';
      case 'invoiced': return '已开票';
      case 'cancelled': return '已取消';
      case 'completed': return '已完成';
      default: return status || '未知';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Check if user can view sales prices (admin, accounting, sales_person, warehouse_manager)
  const canViewPrices = user && ['admin', 'accounting', 'sales_person', 'warehouse_manager'].includes(user.role);


  const headCells = [
    { id: 'sales_order_id', label: '订单编号' },
    { id: 'client_name', label: '客户名称' },
    { id: 'status', label: '状态' },
    { id: 'total_products', label: '产品' },
    ...(canViewPrices ? [{ id: 'total_price', label: '总价' }] : []),
    { id: 'order_date', label: '订单日期' },
    { id: 'delivery_date', label: '交付日期' },
    { id: 'actions', label: '操作', sortable: false },
  ];

  const SortIcon = ({ columnId }) => {
    if (sorting.orderBy !== columnId) return <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground" />;
    return sorting.order === 'asc'
      ? <ArrowUp className="ml-1 h-4 w-4" />
      : <ArrowDown className="ml-1 h-4 w-4" />;
  };

  const getDialogTitle = () => {
    switch (confirmDialog.action) {
      case 'ship': return '确认发货订单';
      case 'confirm': return '确认订单';
      case 'delete': return '删除订单';
      default: return '取消订单';
    }
  };

  const getDialogDescription = () => {
    switch (confirmDialog.action) {
      case 'ship':
        return `确定要发货订单 ${confirmDialog.order?.sales_order_id} 吗？`;
      case 'confirm':
        return `确定要确认订单 ${confirmDialog.order?.sales_order_id} 吗？这将标记为可处理状态。`;
      case 'delete':
        return `确定要永久删除订单 ${confirmDialog.order?.sales_order_id} 吗？此操作不可恢复。`;
      default:
        return `确定要取消订单 ${confirmDialog.order?.sales_order_id} 吗？`;
    }
  };

  const getConfirmButtonVariant = () => {
    if (confirmDialog.action === 'cancel' || confirmDialog.action === 'delete') {
      return 'destructive';
    }
    return 'default';
  };

  const getConfirmButtonText = () => {
    switch (confirmDialog.action) {
      case 'ship': return '发货';
      case 'confirm': return '确认';
      case 'delete': return '删除';
      default: return '取消';
    }
  };

  return (
    <Layout title="销售管理" activeTab="sales">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-foreground">销售订单</h1>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setImportDialogOpen(true)}
              disabled={loading}
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              从文件导入
            </Button>
            <Button
              variant="secondary"
              onClick={() => setTemplateSelectorOpen(true)}
              disabled={loading}
            >
              <Download className="mr-2 h-4 w-4" />
              导出到Excel
            </Button>
            <Button onClick={() => navigate('/sales/new')}>
              <Plus className="mr-2 h-4 w-4" />
              新建订单
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <form onSubmit={handleFilterSubmit} className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    name="sales_order_id"
                    placeholder="按订单编号筛选..."
                    value={filters.sales_order_id}
                    onChange={handleFilterChange}
                    className="pl-10 pr-8"
                  />
                  {filters.sales_order_id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                      onClick={() => handleClearFilter('sales_order_id')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    name="client_name"
                    placeholder="按客户名称筛选..."
                    value={filters.client_name}
                    onChange={handleFilterChange}
                    className="pl-10 pr-8"
                  />
                  {filters.client_name && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                      onClick={() => handleClearFilter('client_name')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="min-w-[150px]">
                <div className="relative">
                  <Input
                    name="status"
                    placeholder="按状态筛选..."
                    value={filters.status}
                    onChange={handleFilterChange}
                    className="pr-8"
                  />
                  {filters.status && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                      onClick={() => handleClearFilter('status')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="h-8 w-px bg-border mx-1" />

              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-1">
                  <Label className="text-xs">开始日期</Label>
                  <Input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                    className="h-9 w-[140px]"
                  />
                </div>
                <span className="text-sm text-muted-foreground">to</span>
                <div className="space-y-1">
                  <Label className="text-xs">结束日期</Label>
                  <Input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                    className="h-9 w-[140px]"
                  />
                </div>
              </div>
                <div className="h-8 w-px bg-border mx-1" />

                {/* Client Type Filter - Domestic/Import */}
                <div className="flex items-center gap-3 min-w-[140px]">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="client-type-filter"
                      checked={clientTypeFilter === 'domestic'}
                      onCheckedChange={(checked) => setClientTypeFilter(checked ? 'domestic' : 'import')}
                    />
                    <div className="flex flex-col">
                      <Label htmlFor="client-type-filter" className="text-xs cursor-pointer">
                        {clientTypeFilter === 'domestic' ? '国内客户' : '进口客户'}
                      </Label>
                      <span className="text-[10px] text-muted-foreground">
                        {clientTypeFilter === 'domestic' ? '仅显示国内' : '仅显示进口'}
                      </span>
                    </div>
                  </div>
                  <Globe className={`h-4 w-4 ${clientTypeFilter === 'domestic' ? 'text-blue-500' : 'text-purple-500'}`} />
                </div>
              {userRole === 'admin' && (
                <>
                  <div className="h-8 w-px bg-border mx-1" />
                  <div className="min-w-[150px]">
                    <Label className="text-xs mb-1 block">贸易类型 (管理员)</Label>
                    <Select value={tradeTypeFilter} onValueChange={setTradeTypeFilter}>
                      <SelectTrigger className="h-9 border-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">全部订单</SelectItem>
                        <SelectItem value="domestic">仅国内</SelectItem>
                        <SelectItem value="international">仅国际出口</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <Button
                type="submit"
                variant="ghost"
                size="icon"
                disabled={loading}
                title="刷新"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleClearAllFilters}
                disabled={loading}
                title="清除所有筛选"
              >
                <X className="h-4 w-4" />
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Orders Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        {headCells.map((headCell) => (
                          <th
                            key={headCell.id}
                            className="px-4 py-3 text-left font-semibold text-foreground"
                          >
                            {headCell.sortable !== false ? (
                              <button
                                className="flex items-center hover:text-primary transition-colors"
                                onClick={() => handleSort(headCell.id)}
                              >
                                {headCell.label}
                                <SortIcon columnId={headCell.id} />
                              </button>
                            ) : (
                              headCell.label
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {orders.map((order) => (
                        <tr key={order._id} className="hover:bg-muted/30">
                          <td className="px-4 py-3">
                            <button
                              className="text-primary underline hover:text-primary/80 transition-colors font-medium"
                              onClick={() => navigate(`/sales/${order._id}/view`)}
                            >
                              {order.sales_order_id}
                            </button>
                          </td>
                          <td className="px-4 py-3">{order.client_name}</td>
                          <td className="px-4 py-3">
                            <Badge variant={getStatusVariant(order.status)}>
                              {getStatusLabel(order.status)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {order.total_products || order.products?.length || 0} 项
                          </td>
                          {canViewPrices && (
                            <td className="px-4 py-3 font-bold">
                              {formatCurrency(order.total_price)}
                            </td>
                          )}
                          <td className="px-4 py-3">{formatDate(order.order_date)}</td>
                          <td className="px-4 py-3">
                            {order.delivery_date ? (
                              formatDate(order.delivery_date)
                            ) : (
                              <span className="text-red-500 font-medium">缺失</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                 <DropdownMenuItem onClick={() => handleAction('view', order)}>
                                   <Eye className="mr-2 h-4 w-4" />
                                   查看详情
                                 </DropdownMenuItem>
                                 {order.status !== 'invoiced' && (
                                   <DropdownMenuItem onClick={() => handleAction('edit', order)}>
                                     <Pencil className="mr-2 h-4 w-4" />
                                     编辑订单
                                   </DropdownMenuItem>
                                 )}
                                 <DropdownMenuItem onClick={() => handleAction('clone', order)}>
                                   <Copy className="mr-2 h-4 w-4" />
                                   克隆订单
                                 </DropdownMenuItem>
                                {order.status !== 'confirmed' && order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'invoiced' && (
                                  <DropdownMenuItem onClick={() => handleAction('confirm', order)}>
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                    确认订单
                                  </DropdownMenuItem>
                                )}
                                {order.status === 'confirmed' && (
                                  <DropdownMenuItem onClick={() => handleAction('ship', order)}>
                                    <Truck className="mr-2 h-4 w-4" />
                                    标记为已发货
                                  </DropdownMenuItem>
                                )}
                                {order.status !== 'shipped' && order.status !== 'delivered' && (
                                  <DropdownMenuItem onClick={() => handleAction('cancel', order)}>
                                    <XCircle className="mr-2 h-4 w-4" />
                                    取消订单
                                  </DropdownMenuItem>
                                )}
                                {userRole === 'admin' && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => handleAction('delete', order)}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      删除订单
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}

                      {orders.length === 0 && (
                        <tr>
                          <td colSpan={canViewPrices ? 8 : 7} className="px-4 py-8 text-center text-muted-foreground">
                            未找到销售订单
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {pagination.total_pages > 1 && (
                  <div className="flex justify-center py-4 border-t">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                      >
                        上一页
                      </Button>
                      <span className="text-sm text-muted-foreground px-4">
                        第 {pagination.page} / {pagination.total_pages} 页
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= pagination.total_pages}
                      >
                        下一页
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <p className="mt-4 text-sm text-muted-foreground">
          显示 {orders.length} / {pagination.total_count} 个订单
        </p>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: '', order: null })}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{getDialogTitle()}</DialogTitle>
              <DialogDescription>{getDialogDescription()}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmDialog({ open: false, action: '', order: null })}
              >
                取消
              </Button>
              <Button
                variant={getConfirmButtonVariant()}
                onClick={handleConfirmAction}
              >
                {getConfirmButtonText()}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Template Selector Dialog */}
        <TemplateSelector
          open={templateSelectorOpen}
          onClose={() => setTemplateSelectorOpen(false)}
          entityType="sales"
          onSelect={async (template) => {
            try {
              const filteredOrders = orders.map(order => order._id);

              const { blob, filename } = await excelExportService.exportUsingTemplate('sales', {
                template_id: template.template_id,
                sales_order_ids: filteredOrders,
                filters: filters
              });

              excelExportService.downloadBlob(blob, filename);
              console.log('Export completed:', filename);
            } catch (error) {
              console.error('Export failed:', error);
              setError('Failed to export data: ' + error.message);
            }
          }}
        />

        <SalesImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          mode="sales_order"
          onImportComplete={() => {
            loadOrders();
          }}
        />
      </div>
    </Layout>
  );
};

export default SalesPage;
